import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../hooks/useToast';
import Toast from './Toast';

const SpecialOffersManagement = ({ isFullscreen = false }) => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const { toasts, showToast, removeToast } = useToast();
  const restaurantId = localStorage.getItem('restaurantData') ? JSON.parse(localStorage.getItem('restaurantData'))._id : null;
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    discountType: 'percentage',
    discountValue: '',
    minOrderAmount: '',
    maxDiscountAmount: '',
    validFrom: '',
    validUntil: '',
    usageLimit: '',
    applicableItems: [],
    customerRestrictions: {
      newCustomersOnly: false,
      minLoyaltyPoints: ''
    }
  });

  useEffect(() => {
    fetchOffers();
    fetchMenuItems();
  }, []);

  const fetchOffers = useCallback(async (showRefreshSpinner = false) => {
    if (!restaurantId) return;
    if (showRefreshSpinner) setIsRefreshing(true);
    try {
      const response = await axios.get(`${API_ENDPOINTS.OFFERS}/admin/${restaurantId}`);
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
      showToast('Failed to load offers', 'error');
    } finally {
      if (showRefreshSpinner) setIsRefreshing(false);
    }
  }, [restaurantId, showToast]);

  const fetchMenuItems = async () => {
    try {
      const response = await axios.get(`${API_ENDPOINTS.MENU}?restaurantId=${restaurantId}`);
      setMenuItems(response.data);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      showToast('Failed to load menu items', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const data = {
        ...formData,
        restaurantId,
        discountValue: parseFloat(formData.discountValue),
        minOrderAmount: parseFloat(formData.minOrderAmount) || 0,
        maxDiscountAmount: formData.maxDiscountAmount ? parseFloat(formData.maxDiscountAmount) : null,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        customerRestrictions: {
          ...formData.customerRestrictions,
          minLoyaltyPoints: parseInt(formData.customerRestrictions.minLoyaltyPoints) || 0
        }
      };

      if (editingOffer) {
        await axios.put(`${API_ENDPOINTS.OFFERS}/admin/${editingOffer._id}`, data);
        showToast('Offer updated successfully!', 'success');
      } else {
        await axios.post(`${API_ENDPOINTS.OFFERS}/admin`, data);
        showToast('Offer created successfully!', 'success');
      }
      
      fetchOffers();
      resetForm();
    } catch (error) {
      console.error('Error saving offer:', error);
      showToast('Failed to save offer. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setShowDeleteConfirm(id);
  };

  const confirmDelete = async (id) => {
    setDeleteLoading(id);
    setShowDeleteConfirm(null);
    try {
      await axios.delete(`${API_ENDPOINTS.OFFERS}/admin/${id}`);
      showToast('Offer deleted successfully!', 'success');
      fetchOffers();
    } catch (error) {
      console.error('Error deleting offer:', error);
      showToast('Failed to delete offer. Please try again.', 'error');
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      title: offer.title,
      description: offer.description,
      discountType: offer.discountType,
      discountValue: offer.discountValue.toString(),
      minOrderAmount: offer.minOrderAmount.toString(),
      maxDiscountAmount: offer.maxDiscountAmount?.toString() || '',
      validFrom: new Date(offer.validFrom).toISOString().slice(0, 16),
      validUntil: new Date(offer.validUntil).toISOString().slice(0, 16),
      usageLimit: offer.usageLimit?.toString() || '',
      applicableItems: offer.applicableItems.map(item => item._id),
      customerRestrictions: {
        newCustomersOnly: offer.customerRestrictions.newCustomersOnly,
        minLoyaltyPoints: offer.customerRestrictions.minLoyaltyPoints.toString()
      }
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      discountType: 'percentage',
      discountValue: '',
      minOrderAmount: '',
      maxDiscountAmount: '',
      validFrom: '',
      validUntil: '',
      usageLimit: '',
      applicableItems: [],
      customerRestrictions: {
        newCustomersOnly: false,
        minLoyaltyPoints: ''
      }
    });
    setEditingOffer(null);
    setShowForm(false);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-stone-100 overflow-x-hidden">
      <div className="sticky top-0 z-10 bg-white border-b border-stone-200 shadow-sm">
        <div className="px-3 py-1.5 sm:px-4 sm:py-1.5">
          <div className="flex flex-wrap items-center justify-between gap-1.5">
            <h2 className="text-base font-bold text-stone-800">Special Offers</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fetchOffers(true)}
                disabled={isRefreshing}
                className="inline-flex items-center justify-center p-2 rounded-lg font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60 transition-colors shadow-sm size-9"
                title="Refresh"
                aria-label="Refresh"
              >
                {isRefreshing ? (
                  <span className="inline-block w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <span className="text-sm">↻</span>
                )}
              </button>
              {!isFullscreen && (
                <button
                  type="button"
                  onClick={() => navigate('/restaurant/offers-fullscreen')}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-stone-200 bg-white text-stone-600 hover:bg-stone-50 transition-colors"
                  title="Fullscreen"
                  aria-label="Fullscreen"
                >
                  <span className="text-xs">⤢</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => { resetForm(); setShowForm(true); }}
                className="h-9 px-3 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600"
              >
                + Create Offer
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-3 sm:p-5">
        {offers.length === 0 ? (
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-6 text-center">
            <p className="text-4xl mb-2">🎁</p>
            <h3 className="text-sm font-bold text-stone-800 mb-1">No offers yet</h3>
            <p className="text-xs text-stone-500 mb-3">Create your first offer to attract customers.</p>
            <button
              type="button"
              onClick={() => setShowForm(true)}
              className="px-3 py-2 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600"
            >
              Create offer
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {offers.map((offer) => {
              const isActive = offer.isActive && new Date() >= new Date(offer.validFrom) && new Date() <= new Date(offer.validUntil);
              return (
                <div
                  key={offer._id}
                  className="bg-white rounded-xl border border-stone-200 shadow-sm p-3 border-l-4 border-l-orange-400 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-bold text-stone-800 truncate">{offer.title}</h3>
                      <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                        {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button type="button" onClick={() => handleEdit(offer)} className="p-1.5 rounded-md text-orange-600 hover:bg-orange-50" aria-label="Edit">✏️</button>
                      <button type="button" onClick={() => handleDelete(offer._id)} disabled={deleteLoading === offer._id} className="p-1.5 rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50" aria-label="Delete">{deleteLoading === offer._id ? '…' : '🗑️'}</button>
                    </div>
                  </div>
                  <p className="text-xs text-stone-600 line-clamp-2 mb-2">{offer.description}</p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] mb-2">
                    <span className="text-stone-500">Discount</span>
                    <span className="font-semibold text-orange-600">{offer.discountType === 'percentage' ? `${offer.discountValue}%` : `₹${offer.discountValue}`}</span>
                    <span className="text-stone-500">Min order</span>
                    <span className="font-medium">₹{offer.minOrderAmount}</span>
                    <span className="text-stone-500">Valid until</span>
                    <span className="font-medium">{formatDate(offer.validUntil)}</span>
                    <span className="text-stone-500">Usage</span>
                    <span className="font-medium">{offer.usedCount}{offer.usageLimit ? `/${offer.usageLimit}` : ''}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-stone-100">
                    <span className="text-[10px] text-stone-400">{formatDate(offer.createdAt)}</span>
                    <button type="button" onClick={() => handleEdit(offer)} className="px-2 py-1 text-[10px] font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600">Edit</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Offer Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={resetForm}
          >
            <motion.div
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col border border-stone-200 overflow-hidden"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-3 sm:px-4 py-2 border-b border-stone-200 bg-stone-50/80 flex-shrink-0">
                <h3 className="text-base font-bold text-stone-800">{editingOffer ? 'Edit Offer' : 'Create Offer'}</h3>
                <button type="button" onClick={resetForm} className="p-1.5 rounded-lg text-stone-500 hover:bg-stone-200 text-lg leading-none">×</button>
              </div>
              <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                <div className="overflow-y-auto flex-1 p-3 sm:p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Title *</p>
                      <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="e.g. Weekend 20% Off" required className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Discount type</p>
                      <select value={formData.discountType} onChange={(e) => setFormData({...formData, discountType: e.target.value})} className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none bg-white">
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed (₹)</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Description *</p>
                    <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} placeholder="Describe the offer..." required className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none resize-none" />
                  </div>

                  <div className="p-2 rounded-lg bg-stone-50 border border-stone-200">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-1.5">Discount</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] text-stone-500">Value {formData.discountType === 'percentage' ? '(%)' : '(₹)'}</label>
                        <input type="number" value={formData.discountValue} onChange={(e) => setFormData({...formData, discountValue: e.target.value})} min="0" step="0.01" placeholder={formData.discountType === 'percentage' ? '20' : '100'} required className="w-full px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none mt-0.5" />
                      </div>
                      <div>
                        <label className="text-[10px] text-stone-500">Min order (₹)</label>
                        <input type="number" value={formData.minOrderAmount} onChange={(e) => setFormData({...formData, minOrderAmount: e.target.value})} min="0" step="0.01" placeholder="500" className="w-full px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none mt-0.5" />
                      </div>
                      <div>
                        <label className="text-[10px] text-stone-500">Max discount (₹)</label>
                        <input type="number" value={formData.maxDiscountAmount} onChange={(e) => setFormData({...formData, maxDiscountAmount: e.target.value})} min="0" step="0.01" placeholder="200" className="w-full px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none mt-0.5" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Valid from</p>
                      <input type="datetime-local" value={formData.validFrom} onChange={(e) => setFormData({...formData, validFrom: e.target.value})} required className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Valid until</p>
                      <input type="datetime-local" value={formData.validUntil} onChange={(e) => setFormData({...formData, validUntil: e.target.value})} required className="w-full px-2.5 py-2 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none" />
                    </div>
                  </div>

                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500 mb-0.5">Usage limit (optional)</p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <input type="number" value={formData.usageLimit} onChange={(e) => setFormData({...formData, usageLimit: e.target.value})} min="1" placeholder="Unlimited" className="w-24 px-2.5 py-1.5 text-xs border border-stone-200 rounded-lg focus:ring-2 focus:ring-orange-400 outline-none" />
                      <button type="button" onClick={() => setFormData({...formData, usageLimit: '50'})} className="px-2 py-1 text-[10px] font-medium rounded bg-stone-100 text-stone-700 hover:bg-stone-200">50</button>
                      <button type="button" onClick={() => setFormData({...formData, usageLimit: '100'})} className="px-2 py-1 text-[10px] font-medium rounded bg-stone-100 text-stone-700 hover:bg-stone-200">100</button>
                      <button type="button" onClick={() => setFormData({...formData, usageLimit: ''})} className="px-2 py-1 text-[10px] font-medium rounded bg-stone-100 text-stone-700 hover:bg-stone-200">Unlimited</button>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 px-3 sm:px-4 py-2.5 border-t border-stone-200 bg-white flex-shrink-0">
                  <button type="button" onClick={resetForm} className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50">Cancel</button>
                  <button type="submit" disabled={loading} className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50">
                    {loading ? 'Saving...' : (editingOffer ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              className="bg-white rounded-xl shadow-xl max-w-sm w-full border border-stone-200 overflow-hidden"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-3 sm:px-4 py-3 border-b border-stone-200 bg-stone-50/80">
                <h3 className="text-sm font-bold text-stone-800">Delete Offer</h3>
              </div>
              <p className="px-3 sm:px-4 py-3 text-xs text-stone-600">
                Are you sure you want to delete this offer? This cannot be undone.
              </p>
              <div className="flex gap-2 px-3 sm:px-4 py-2.5 border-t border-stone-200 bg-white">
                <button type="button" onClick={() => setShowDeleteConfirm(null)} className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50">Cancel</button>
                <button type="button" onClick={() => confirmDelete(showDeleteConfirm)} className="flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-red-500 text-white hover:bg-red-600">Delete</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast Notifications */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

export default SpecialOffersManagement;